import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import { SingleValue } from 'react-select';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { CustomerBo } from '@utils/types/back-office/customer-bo.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import CountrySelect, {
  CountryOption
} from '@components/shared/country-code.tsx';
import { PATHS } from '../../../../router/paths';

interface Props {
  register: UseFormRegister<CustomerBo>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CustomerBo, any>;
  errors: FieldErrors<CustomerBo>;
  setValue: UseFormSetValue<CustomerBo>;
  customer?: CustomerBo;
  cardStyle: React.CSSProperties;
  currencyConflict: boolean;
  setCurrencyConflict: (v: boolean) => void;
  currencyConflictMessage: string | null;
  setCurrencyConflictMessage: (v: string | null) => void;
}

export function CustomerBillingTab({
  register,
  control,
  errors,
  setValue,
  customer,
  cardStyle,
  currencyConflict,
  setCurrencyConflict,
  currencyConflictMessage,
  setCurrencyConflictMessage
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="py-4">
      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <PageHeader
          title={t('billing-details')}
          caption={t('use-a-permanent-address-where-you-can-receive-mail')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 lg:gap-6">
          <Input
            id="billing_contact_firstname"
            label={t('first-name')}
            defaultValue={customer?.billing_contact_firstname || ''}
            type="text"
            autoComplete="first-name"
            {...register('billing_contact_firstname')}
            error={errors?.billing_contact_firstname?.message}
          />
          <Input
            id="billing_contact_lastname"
            label={t('last-name')}
            defaultValue={customer?.billing_contact_lastname || ''}
            type="text"
            autoComplete="last-name"
            {...register('billing_contact_lastname')}
            error={errors?.billing_contact_lastname?.message}
          />
          <Input
            id="address_street"
            label={t('street-address')}
            defaultValue={customer?.address_street || ''}
            type="text"
            autoComplete="street-address"
            {...register('address_street')}
            error={errors?.address_street?.message}
          />
          <Input
            id="address_city"
            label={t('address-city')}
            defaultValue={customer?.address_city || ''}
            type="text"
            autoComplete="city"
            {...register('address_city')}
            error={errors?.address_city?.message}
          />
          <Input
            id="address_postal_code"
            label={t('postal-code')}
            defaultValue={customer?.address_postal_code || ''}
            type="text"
            autoComplete="postal-code"
            {...register('address_postal_code')}
            error={errors?.address_postal_code?.message}
          />
          <CountrySelect
            label={t('country')}
            defaultValue={customer?.address_country_code || ''}
            onChange={(option: SingleValue<CountryOption>) => {
              setValue('address_country_code', option?.value || '');
            }}
            error={errors.address_country_code?.message}
          />
          <Input
            id="billing_ref"
            label={t('invoice-ref-identifier-to-add-to-every-invoice')}
            defaultValue={customer?.billing_ref || ''}
            type="text"
            autoComplete="billing-ref"
            {...register('billing_ref')}
            error={errors?.billing_ref?.message}
          />
          <Input
            id="billing_vat"
            label={t('vat-number')}
            defaultValue={customer?.billing_vat || ''}
            type="text"
            autoComplete="billing-vat"
            {...register('billing_vat')}
            error={errors?.billing_vat?.message}
          />
        </div>
      </div>

      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6 mt-4"
        style={cardStyle}
      >
        <PageHeader
          title={t('invoice-currency')}
          caption={t('select-currency-for-invoices')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <Controller
          control={control}
          name="invoice_currency"
          defaultValue={customer?.invoice_currency ?? 'EUR'}
          render={({ field: { onChange, value } }) => (
            <div className="space-y-3">
              <div className="flex items-center gap-6">
                {(['EUR', 'USD'] as const).map(code => (
                  <label
                    key={code}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <input
                      type="radio"
                      value={code}
                      checked={value === code}
                      onChange={() => {
                        onChange(code);
                        setCurrencyConflict(false);
                        setCurrencyConflictMessage(null);
                      }}
                      className="h-4 w-4 border-hairline text-primary focus:ring-primary"
                    />
                    <span className="font-medium">
                      {code === 'EUR' ? '€ EUR' : '$ USD'}
                    </span>
                  </label>
                ))}
              </div>
              {currencyConflict && (
                <div className="flex items-start gap-2 rounded-2xl border px-3 py-2 bg-amber-400/10 border-amber-400/30 dark:bg-amber-400/15 dark:border-amber-400/40">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    {currencyConflictMessage
                      ? currencyConflictMessage
                      : t('currency-change-blocked-open-invoices') ||
                        'Currency cannot be changed while there are open invoices.'}{' '}
                    <Link
                      to={PATHS().BILLABLES}
                      className="underline font-medium hover:opacity-80"
                    >
                      {t('view-open-invoices') || 'View open invoices'}
                    </Link>
                  </p>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
